import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import { catchError, map, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { Permission, FeatureHierarchy } from '../graphql/generated/graphql';

export const STATIC_CREDENTIALS = {
  email: 'bilal123@4iisolutions.com',
  // email: 'wali@test.com',
  // email: 'nabeel@4iisolutions.com',
  // email: 'apr@mail.com',
  // email: 'javed.waqas@gmail.com',
  password: 'Test@123',
};

interface AccessToken {
  token: string;
  expires: string;
}

interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  logo?: string;
  avatar_location?: string;
  date_format?: string;
  currency_symbol?: string;
  currency_format?: string;
  isOwner?: boolean;
  user_is_super_admin?: boolean;
  business_is_super_admin?: boolean;
  is_popup_closed?: boolean;
  is_sample_data?: boolean;
  two_fa_secret?: string;
  business_id: {
    _id: string;
    logo?: string;
    business_legal_name?: string;
    business_type?: string;
    time_zone?: string;
    is_24_hour_format?: boolean;
    business_package?: { hr_module_only?: boolean } | null;
  } | null;
  business_status: string;
  business_type?: string;
}

interface LoginResult {
  tokens: {
    access: AccessToken;
    refresh: AccessToken;
  };
  user: AuthUser;
}

interface RefreshTokenResult {
  refreshToken: {
    tokens: {
      access: AccessToken;
      refresh: AccessToken;
    };
  };
}

interface RefreshTokenResponse {
  data: RefreshTokenResult;
}

interface LoginResponse {
  data: {
    loginWithEmail: LoginResult;
  };
}

interface LoginToOtherBusinessInput {
  business_id?: string;
  [key: string]: unknown;
}

interface LoginToOtherBusinessResponse {
  data: {
    loginToOtherBusiness: LoginResult;
  };
}

interface PermissionsResult {
  getLoggedInUserAllowModulesAndFeature: {
    modules?: string[] | null;
    features?: string[] | null;
    permission?: Permission[] | null;
    feature_hierarchy?: FeatureHierarchy[] | null;
  } | null;
}

const GET_PERMISSIONS = gql`
  query getLoggedInUserAllowModulesAndFeature {
    getLoggedInUserAllowModulesAndFeature {
      feature_hierarchy {
        feature
        is_all
        is_peer
        is_under_them
        is_only_me
      }
      modules
      features
      permission {
        module
        feature
        permission
        permission_at
      }
    }
  }
`;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apollo = inject(Apollo);

  readonly allowedModules = signal<string[]>([]);
  readonly allowedFeatures = signal<string[]>([]);
  readonly allowedFeatureActions = signal<Permission[]>([]);
  readonly featureHierarchy = signal<FeatureHierarchy[]>([]);
  readonly permissionsLoaded = signal(false);

  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  getToken(): string | null {
    const value = localStorage.getItem('access_token');
    try {
      return JSON.parse(value as string) as string;
    } catch {
      return null;
    }
  }

  getUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem('user');
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  }

  login(email: string, password: string): import('rxjs').Observable<LoginResponse> {
    const body = {
      query: `
        mutation loginWithEmail($email: String!, $password: String!) {
          loginWithEmail(email: $email, password: $password) {
             tokens {
      access {
        token
        expires
      }
      refresh {
        token
        expires
      }
    }
    user {
    is_navigated_user
    date_format
     currency_format
    currency_name
    currency_symbol
    do_not_disturb
    two_fa_secret
    is_un_restricted
    last_working_date
    is_sample_data
    isOwner
      id
        profile_pictures {
        dimension
        path
      }
      business_id {
        zip_code
        is_24_hour_format
         date_format
      time_zone
      currency_format
      currency_name
        Country {
        name
        _id
      } 
      state {
        _id
        state_name
      }
      city {
        _id
        city_name
      }
        _id
         logo
      business_legal_name
        business_type
        time_zone
        business_package {
          hr_module_only
        }
      }
      business_status
      business_type
      first_name
      last_name
      email
      phone
      logo
      avatar_location
      user_is_super_admin
      is_popup_closed
      business_is_super_admin

    }
    rocketChat {
      userId
      authToken
      refreshToken
    }
    businesses {
        pictures {
        dimension
        path
      }
      business_type
      _id
      business_email
      business_legal_name
      address
      logo
      number_of_stores
      date_format
      time_zone
      Country {
        _id
        name
        short_name
      }
      status
      city {
        _id
        city_name
      }
    }
    
          }
        }
      `,
      variables: { email, password },
    };

    return this.http.post<LoginResponse>(environment.graphqlURI, body).pipe(
      tap(res => {
        if (res?.data?.loginWithEmail) {
          this.storeAuthData(res.data.loginWithEmail);
        }
      })
    );
  }

  loginToOtherBusiness(input: LoginToOtherBusinessInput): import('rxjs').Observable<LoginToOtherBusinessResponse> {
    const body = {
      query: `
        mutation LoginToOtherBusiness($input: loginToOtherBusinessInput) {
          loginToOtherBusiness(input: $input) {
            tokens {
              access {
                token
                expires
              }
              refresh {
                token
                expires
              }
            }
            user {
              is_navigated_user
              date_format
              currency_format
              currency_name
              currency_symbol
              do_not_disturb
              two_fa_secret
              is_un_restricted
              last_working_date
              is_sample_data
              isOwner
              id
              profile_pictures {
                dimension
                path
              }
              business_id {
                pictures {
                  dimension
                  path
                }
                zip_code
                Country {
                  name
                  _id
                }
                state {
                  _id
                  state_name
                }
                city {
                  _id
                  city_name
                }
                _id
                logo
                business_legal_name
                business_type
                time_zone
                business_package {
                  hr_module_only
                }
              }
              business_status
              business_type
              first_name
              last_name
              email
              phone
              logo
              avatar_location
              user_is_super_admin
              is_popup_closed
              business_is_super_admin
            }
            rocketChat {
              userId
              authToken
              refreshToken
            }
          }
        }
      `,
      variables: { input },
    };

    return this.http.post<LoginToOtherBusinessResponse>(environment.graphqlURI, body).pipe(
      tap(res => {
        if (res?.data?.loginToOtherBusiness) {
          this.storeAuthData(res.data.loginToOtherBusiness);
        }
      })
    );
  }

  storeAuthData(data: LoginResult): void {
    this.apollo.client.cache.reset();
    localStorage.setItem('access_token', JSON.stringify(data.tokens.access.token));
    localStorage.setItem('refresh_token', JSON.stringify(data.tokens.refresh.token));
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('dateformat', JSON.stringify(data.user.date_format ?? null));
    localStorage.setItem('currencySymbol', JSON.stringify(data.user.currency_symbol ?? null));
    localStorage.setItem('currencyFormat', JSON.stringify(data.user.currency_format ?? null));
    if (data.user?.business_id?._id) {
      localStorage.setItem('business', data.user.business_id._id);
    }
  }

  public isActionAllowed(feature: string, action: string): boolean {
    if (this.allowedFeatureActions) {
      return this.allowedFeatureActions().findIndex(featureAction => featureAction.feature === feature && featureAction.permission === action) !== -1
    }
    return false
  }

  loadPermissions(): import('rxjs').Observable<boolean> {
    return this.apollo
      .query<PermissionsResult>({ query: GET_PERMISSIONS, fetchPolicy: 'network-only' })
      .pipe(
        map(res => {
          const data = res.data?.getLoggedInUserAllowModulesAndFeature;
          if (!data?.modules?.length) {
            this.allowedModules.set([]);
            this.allowedFeatures.set([]);
            this.allowedFeatureActions.set([]);
            this.featureHierarchy.set([]);
            this.permissionsLoaded.set(true);
            return false;
          }
          this.allowedModules.set((data.modules ?? []).filter(Boolean) as string[]);
          this.allowedFeatures.set((data.features ?? []).filter(Boolean) as string[]);
          this.allowedFeatureActions.set((data.permission ?? []).filter(Boolean) as Permission[]);
          this.featureHierarchy.set((data.feature_hierarchy ?? []).filter(Boolean) as FeatureHierarchy[]);
          this.permissionsLoaded.set(true);
          return true;
        }),
      );
  }

  validatePermission(data: Record<string, string>): boolean {
    if (!data || Object.keys(data).length === 0) return true;

    if (data['feature']) {
      return this.allowedFeatures().includes(data['feature']);
    }

    if (data['permission']) {
      const user = this.getUser();
      if (user?.business_is_super_admin && data['permission'] === 'can_add_package_to_package_listing_list') {
        return true;
      }
      return this.allowedFeatureActions().some(p => p.permission === data['permission']);
    }

    if (data['menu']) {
      return this.allowedModules().includes(data['menu']);
    }

    return false;
  }

  clearPermissions(): void {
    this.allowedModules.set([]);
    this.allowedFeatures.set([]);
    this.allowedFeatureActions.set([]);
    this.featureHierarchy.set([]);
    this.permissionsLoaded.set(false);
  }

  refreshToken(): import('rxjs').Observable<RefreshTokenResponse> {
    const storedRefreshToken = this.getRefreshToken();
    const body = {
      query: `
        mutation refreshToken($refreshToken: String!) {
          refreshToken(refreshToken: $refreshToken) {
            tokens {
              access { token expires }
              refresh { token expires }
            }
          }
        }
      `,
      variables: { refreshToken: storedRefreshToken },
    };

    return this.http.post<RefreshTokenResponse>(environment.graphqlURI, body).pipe(
      tap(res => {
        if (res?.data?.refreshToken) {
          const tokens = res.data.refreshToken.tokens;
          localStorage.setItem('access_token', JSON.stringify(tokens.access.token));
          localStorage.setItem('refresh_token', JSON.stringify(tokens.refresh.token));
        }
      }),
      catchError(err => {
        this.signout();
        return throwError(() => err);
      })
    );
  }

  getRefreshToken(): string | null {
    const value = localStorage.getItem('refresh_token');
    try {
      return JSON.parse(value as string) as string;
    } catch {
      return null;
    }
  }

  signout(): void {
    this.clearPermissions();
    localStorage.clear();
    sessionStorage.clear();
    this.router.navigateByUrl('/login');
  }
}
